import React, { useState, useRef, useCallback } from 'react'
import {
  FaBug,
  FaTimes,
  FaCamera,
  FaTrash,
  FaPaperPlane,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa'
import { toast } from 'react-toastify'
import html2canvas from 'html2canvas'
import bugReportService from '../services/bugReportService'

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-800' },
]

const CATEGORY_OPTIONS = [
  { value: 'ui', label: 'UI / Frontend' },
  { value: 'api', label: 'API / Backend' },
  { value: 'general', label: 'General' },
]

export default function BugReportForm({ onSuccess, getActivityLog, getEnvironment }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [category, setCategory] = useState('general')
  const [stepsToReproduce, setStepsToReproduce] = useState('')
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showContext, setShowContext] = useState(false)
  const [contextData, setContextData] = useState(null)
  const [activityLog, setActivityLog] = useState([])
  const fileInputRef = useRef(null)

  const loadContext = useCallback(() => {
    if (getEnvironment) setContextData(getEnvironment())
    if (getActivityLog) setActivityLog(getActivityLog())
    setShowContext(true)
  }, [getEnvironment, getActivityLog])

  const removeActivityEntry = (index) => {
    setActivityLog((prev) => prev.filter((_, i) => i !== index))
  }

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be under 5MB')
      return
    }

    setScreenshot(file)
    const reader = new FileReader()
    reader.onload = (ev) => setScreenshotPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const captureScreenshot = async () => {
    // Save form state — html2canvas DOM cloning can trigger React reconciliation
    const savedState = {
      title,
      description,
      severity,
      category,
      stepsToReproduce,
    }

    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 1,
        logging: false,
      })

      // Restore form state after capture
      setTitle(savedState.title)
      setDescription(savedState.description)
      setSeverity(savedState.severity)
      setCategory(savedState.category)
      setStepsToReproduce(savedState.stepsToReproduce)

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'screenshot.png', { type: 'image/png' })
          setScreenshot(file)
          setScreenshotPreview(canvas.toDataURL())
        }
      })
    } catch {
      toast.error('Screenshot capture failed. Try uploading manually.')
      fileInputRef.current?.click()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required')
      return
    }

    setSubmitting(true)

    try {
      const formData = new FormData()
      formData.append('title', title.trim())
      formData.append('description', description.trim())
      formData.append('severity', severity)
      formData.append('category', category)
      if (stepsToReproduce.trim()) {
        formData.append('stepsToReproduce', stepsToReproduce.trim())
      }
      if (contextData) {
        formData.append('environment', JSON.stringify(contextData))
      }
      if (activityLog.length > 0) {
        formData.append('activityLog', JSON.stringify(activityLog))
      }
      if (screenshot) {
        formData.append('screenshot', screenshot)
      }

      const result = await bugReportService.create(formData)

      toast.success(
        <div>
          Bug report filed!{' '}
          <a
            href={result.issue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-semibold"
          >
            View on GitHub →
          </a>
        </div>,
      )

      // Reset form
      setTitle('')
      setDescription('')
      setSeverity('medium')
      setCategory('general')
      setStepsToReproduce('')
      setScreenshot(null)
      setScreenshotPreview(null)
      setShowContext(false)
      setContextData(null)
      setActivityLog([])

      if (onSuccess) onSuccess()
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to file bug report'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FaBug className="text-red-500 text-xl" />
        <h3 className="text-lg font-semibold text-gray-900">File a Bug Report</h3>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief summary of the issue"
          className="input-field w-full"
          required
          disabled={submitting}
        />
      </div>

      {/* Severity + Category row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="input-field w-full"
            disabled={submitting}
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input-field w-full"
            disabled={submitting}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the bug in detail — what happened, what you expected, and what went wrong."
          className="input-field w-full h-32 resize-y"
          required
          disabled={submitting}
        />
      </div>

      {/* Steps to Reproduce */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Steps to Reproduce <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          value={stepsToReproduce}
          onChange={(e) => setStepsToReproduce(e.target.value)}
          placeholder={'1. Go to ...\n2. Click on ...\n3. See error'}
          className="input-field w-full h-24 resize-y"
          disabled={submitting}
        />
      </div>

      {/* Screenshot */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Screenshot</label>
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={captureScreenshot}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
            disabled={submitting}
          >
            <FaCamera /> Capture Page
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
            disabled={submitting}
          >
            Upload Image
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleScreenshotChange}
            className="hidden"
          />
        </div>
        {screenshotPreview && (
          <div className="relative inline-block">
            <img
              src={screenshotPreview}
              alt="Screenshot preview"
              className="max-h-48 rounded-lg border border-gray-200"
            />
            <button
              type="button"
              onClick={() => {
                setScreenshot(null)
                setScreenshotPreview(null)
              }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* Environment Context */}
      <div className="border border-gray-200 rounded-lg">
        <button
          type="button"
          onClick={() => (showContext ? setShowContext(false) : loadContext())}
          className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
        >
          <span>Environment Context (auto-captured)</span>
          {showContext ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {showContext && contextData && (
          <div className="px-3 pb-3 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-gray-600">
              <div>
                <span className="font-medium">Browser:</span> {contextData.browser}
              </div>
              <div>
                <span className="font-medium">OS:</span> {contextData.os}
              </div>
              <div>
                <span className="font-medium">Screen:</span> {contextData.screenResolution}
              </div>
              <div className="col-span-2">
                <span className="font-medium">URL:</span>{' '}
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                  {contextData.currentUrl}
                </code>
              </div>
            </div>

            {contextData.consoleErrors?.length > 0 && (
              <div>
                <p className="font-medium text-red-600 mb-1">Console Errors:</p>
                <div className="bg-red-50 rounded p-2 text-xs text-red-800 font-mono space-y-1">
                  {contextData.consoleErrors.map((err, i) => (
                    <div key={i}>{err}</div>
                  ))}
                </div>
              </div>
            )}

            {activityLog.length > 0 && (
              <div>
                <p className="font-medium text-gray-700 mb-1">
                  Recent Actions ({activityLog.length}):
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {activityLog.map((entry, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 text-xs"
                    >
                      <span>
                        <span className="font-medium text-gray-500">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </span>{' '}
                        <span className="text-indigo-600">{entry.action}</span>{' '}
                        <span className="text-gray-600">{entry.details?.substring(0, 60)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeActivityEntry(i)}
                        className="text-gray-400 hover:text-red-500 ml-2"
                        title="Remove this entry"
                      >
                        <FaTimes className="text-[10px]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting || !title.trim() || !description.trim()}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
        >
          {submitting ? (
            <>
              <span className="animate-spin">⏳</span> Filing...
            </>
          ) : (
            <>
              <FaPaperPlane /> File Bug Report
            </>
          )}
        </button>
      </div>
    </form>
  )
}
